import { Injectable } from '@angular/core';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import { GraphQLActionClient, GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';

// ─────────────────────────────────────────────────────────────────────────────
// Data shapes returned by the Outbound actions (mirrors
// packages/Marketing/src/providers/reply/reply-types.ts — kept as local
// read-only projections so this UI package has no dependency on the
// server-side Marketing package).
// ─────────────────────────────────────────────────────────────────────────────

/** Aggregate email-outcome stats from 'Outbound: Get Reply Stats' (EmailOverview output param). */
export interface OutboundEmailOverview {
    contacted: number;
    delivered: number;
    opened: number;
    replied: number;
    interested: number;
    bounced: number;
    optedOut: number;
    meetingsBooked: number;
    deliveredPercentage: number;
    openedPercentage: number;
    repliedPercentage: number;
    interestedPercentage: number;
    bouncedPercentage: number;
}

/** Booked-meeting record from 'Outbound: Get Reply Stats' (Meetings output param). */
export interface OutboundMeeting {
    contactFullName: string;
    sequenceName: string;
    bookedAt: string;
    source: string;
}

/** Combined payload of 'Outbound: Get Reply Stats'. */
export interface OutboundReplyStats {
    overview: OutboundEmailOverview;
    meetings: OutboundMeeting[];
    integration: string;
}

/** Sequence list item from 'Outbound: Get Reply Sequences' (Sequences output param). */
export interface OutboundSequence {
    id: number;
    name: string;
    status: string;
    isArchived: boolean;
    created: string;
    health: string;
}

/** Per-contact engagement row from 'Outbound: Get Email Activity' (Activity output param). */
export interface OutboundEmailActivityRow {
    contactId: number;
    firstName: string;
    lastName: string;
    email: string;
    company: string;
    sequenceId: number;
    sequenceName: string;
    sequenceStepNumber: number;
    sequenceStepDisplayName: string;
    emailAccountEmail: string;
    /** When the email was sent. Reply does not expose a per-open timestamp. */
    deliveryDate: string;
    isDelivered: boolean;
    isOpened: boolean;
    isClicked: boolean;
    isReplied: boolean;
    isBounced: boolean;
    isInterested: boolean;
    isOptedOut: boolean;
}

/** One page of engagement rows from 'Outbound: Get Email Activity'. */
export interface OutboundEmailActivityPage {
    rows: OutboundEmailActivityRow[];
    /** true when another page exists — advance skip by the page size. */
    hasMore: boolean;
}

export interface OutboundStepVariant {
    /** Reply variant id — the target of an in-place edit. null when Reply omitted it. */
    variantId: number | null;
    /** Reply step id that owns this variant — needed to target the PUT endpoint. null when omitted. */
    stepId: number | null;
    subject: string;
    /** Variant body (HTML). Reply returns this as `message`. */
    body: string;
    isEnabled: boolean;
}

export interface OutboundSequenceStep {
    stepNumber: number;
    type: string;
    delayInMinutes: number;
    /** Cumulative days from sequence start: sum of all prior step delays / 1440 */
    dayFromStart: number;
    variants: OutboundStepVariant[];
}

export interface OutboundStepPerf {
    stepNumber: number;
    stepDisplayName: string;
    sent: number;
    opens: number;
    openRate: number;
    replies: number;
    replyRate: number;
    interested: number;
    bounced: number;
    optedOut: number;
}

export interface OutboundSequenceDetail {
    steps: OutboundSequenceStep[];
    perf: OutboundStepPerf[];
}

export interface OutboundPersonaConfig {
    personaName: string;
    sendingDomain: string;
}

/**
 * Discriminated outcome of an Outbound action call.
 * - 'success'      → parsed, typed data
 * - 'not-deployed' → the action record doesn't exist in this environment's
 *                    metadata yet (records not synced) — render the
 *                    "not yet deployed" panel, not an error.
 * - 'error'        → the action exists but the call failed; message carries
 *                    the server-side error text for display.
 */
export type OutboundActionOutcome<T> =
    | { kind: 'success'; data: T }
    | { kind: 'not-deployed'; message: string }
    | { kind: 'error'; message: string };

/**
 * Typed client interface for the Outbound actions. The dashboard codes
 * against this abstraction so the GraphQL plumbing (or a future stub /
 * mock) can be swapped without touching the component.
 */
/** Per-step report from 'Outbound: Create Reply Sequence' (Steps output param). */
export interface OutboundCreatedStep {
    touchNumber: number;
    stepId: number | null;
    variantCount: number;
    degradations: string[];
}

/** Result of 'Outbound: Create Reply Sequence'. The sequence is created PAUSED. */
export interface OutboundCreateResult {
    sequenceId: number;
    status: string;
    dashboardUrl: string;
    steps: OutboundCreatedStep[];
    warnings: string[];
    integration: string;
}

/** Result of 'Outbound: Update Reply Sequence' (DryRun=true returns the plan with zero writes). */
export interface OutboundUpdateResult {
    sequenceId: number;
    stepCount: number;
    updated: number;
    created: number;
    warnings: string[];
    wasDryRun: boolean;
    integration: string;
}

/** Result of 'Outbound: Update Sequence Variant' (in-place single-variant edit). */
export interface OutboundVariantUpdateResult {
    updated: boolean;
    integration: string;
}

/** One persona plan entry from 'Outbound: Enroll People Into Sequences' (Plan output). */
export interface OutboundEnrollPersonaPlan {
    persona: string;
    sequenceId: number;
    contactCount: number;
    contacts: Array<{ email: string; companyAcronym: string | null }>;
}

/** One skipped-person entry from the enroll Plan. */
export interface OutboundEnrollSkipped {
    email: string | null;
    persona: string | null;
    reason: string;
}

/** Full Plan payload from 'Outbound: Enroll People Into Sequences'. */
export interface OutboundEnrollPlan {
    companyId: string;
    personas: OutboundEnrollPersonaPlan[];
    orgAcronyms: Array<{ orgKey: string; acronym: string | null }>;
    skipped: OutboundEnrollSkipped[];
    totalPeople: number;
    eligibleContacts: number;
}

/** Combined result of 'Outbound: Enroll People Into Sequences'. */
export interface OutboundEnrollResult {
    plan: OutboundEnrollPlan;
    enrolledCount: number;
    wasDryRun: boolean;
    integration: string;
}

/** One Apollo label/list from 'Outbound: Get Apollo Lists'. */
export interface OutboundApolloList {
    id: string;
    name: string;
    kind: 'contacts' | 'accounts' | string;
    cachedCount: number;
}

/** One stream config entry from CompanyIntegration.Configuration.streams[]. */
export interface OutboundStreamConfig {
    apolloListName: string;
    personaName: string;
    replySequenceId: number;
    /** Optional sending domain to scope this stream's sends (#1071 P8). */
    sendingDomain?: string;
    /** Optional per-domain daily send cap (#1071 P8). */
    dailyDomainCap?: number;
}

// ─── Recommendations panel (#1071 P8) ──────────────────────────────────────

/** The op carried in a MarketingRecommendation.ActionPayload. */
export type OutboundRecommendationOp = 'pause' | 'scale' | 'iterate' | 'unknown';

/** One MarketingRecommendation row, as surfaced in the dashboard panel. */
export interface OutboundRecommendation {
    id: string;
    category: string;
    title: string;
    detail: string;
    /** Open | Applied | Dismissed. */
    status: string;
    /** Parsed op from ActionPayload (pause/scale are actionable; iterate is advisory). */
    op: OutboundRecommendationOp;
    /** True when the op can be one-click Applied (pause | scale). */
    actionable: boolean;
    createdAt: string;
}

/** Result of 'Outbound: Apply Sequence Recommendation'. */
export interface OutboundApplyResult {
    applied: boolean;
    op: string;
    message: string;
    integration: string;
}

// ─── Central Pool panel (#1071 P8) ──────────────────────────────────────────

/** One OutboundMeeting row from the central pool (via 'Query Records'). */
export interface OutboundPoolMeeting {
    id: string;
    sourceBrand: string;
    prospectEmail: string | null;
    sequenceId: number | null;
    assignedRep: string | null;
    bookedAt: string | null;
    status: string;
}

/** Result of 'Outbound: Ingest Outbound Meetings'. */
export interface OutboundIngestResult {
    ingested: number;
    skipped: number;
    alreadyPresent: number;
    wasDryRun: boolean;
    integration: string;
}

// ─── Variant Rollup view (#1071 P8) ─────────────────────────────────────────

export type OutboundVariantVerdict = 'KEEP' | 'ITERATE' | 'KILL' | 'INSUFFICIENT_DATA';

/** GA4 click→meeting metrics for one (step, variant) — variant-attributable. */
export interface OutboundVariantGA4 {
    sessions: number;
    engagedSessions: number;
    conversions: number;
}

/** One comparative rollup row, per (step, variant). */
export interface OutboundVariantRollupRow {
    step: number;
    variantId: string;
    ga4: OutboundVariantGA4;
    /** Step-level reply rate (%), shared across the step's variants. null when no Reply stats. */
    stepReplyRate: number | null;
    /** Always true — flags that stepReplyRate is NOT variant-attributable. */
    replyRateIsStepLevel: boolean;
    /** Caveat repeating the step-level reply-rate limitation. */
    note: string;
    verdict: OutboundVariantVerdict;
}

/** Per-step reply-rate summary (the STEP axis, variant-blind). */
export interface OutboundStepReplyRate {
    step: number;
    stepDisplayName: string;
    sent: number;
    replies: number;
    replyRate: number;
    winnerLabel: string | null;
    comparisonLabel: string;
}

/** Combined payload of 'Outbound: Get Variant Rollup'. */
export interface OutboundVariantRollup {
    variants: OutboundVariantRollupRow[];
    stepReplyRates: OutboundStepReplyRate[];
}

/** Per-stream result from 'Outbound: Sync Outbound Streams'. */
export interface OutboundStreamSyncResult {
    apolloListName: string;
    personaName: string;
    replySequenceId: number;
    apolloContacts: number;
    imported: number;
    importFailed: number;
    alreadyEnrolled: number;
    enrolled: number;
    enrollFailed: number;
}

/** Combined result from 'Outbound: Sync Outbound Streams'. */
export interface OutboundSyncResult {
    results: OutboundStreamSyncResult[];
    totalImported: number;
    totalEnrolled: number;
    totalFailed: number;
    wasDryRun: boolean;
}

// ─── Sequence Builder (SA2) ─────────────────────────────────────────────────

/**
 * One editable card the Sequence Builder renders. Mirrors the server's
 * BuilderStep (packages/Marketing/src/actions/business/outbound-step-shape.ts)
 * as a local read-only projection — Outbound-UI never imports server code.
 *
 * `body` is Reply-ready HTML (e.g. `<p>…</p>`); the builder converts it to
 * plain text for editing and re-serializes back to the simple markdown format.
 */
export interface BuilderStep {
    /** Day offset from sequence start (day 0 = first send). */
    delayDays: number;
    /** Human-readable delay label, e.g. "Send immediately" / "4 days later". */
    delayLabel: string;
    /** Subject-line variants (empty for thread replies / non-email steps). */
    subjects: string[];
    /** Email body as Reply-ready HTML. */
    body: string;
}

/** Result of 'Outbound: Generate Outbound Copy' — CopyDocument is authoritative. */
export interface GenerateOutboundCopyResult {
    /** Parser-valid simple-format markdown (the source of truth). */
    copyDocument: string;
    /** Editable-card structure derived from the doc by the server. */
    steps: BuilderStep[];
    /** Voice-lint findings raised at generation time (empty when clean). */
    voiceWarnings: string[];
    /** True when the draft was regenerated to clear a voice error. */
    regenerated: boolean;
}

/** Voice-lint severities exactly as the server engine emits them. */
export type VoiceFindingSeverity = 'error' | 'warning';
/** Where a finding occurs within a touch. */
export type VoiceFindingWhere = 'subject' | 'body';

/**
 * One structured voice-lint finding from 'Outbound: Lint Sequence Copy'.
 * Mirrors the server's VoiceLintFinding — the UI maps it to a friendly label
 * and inline highlight; it NEVER re-implements the rules.
 */
export interface VoiceFinding {
    /** Stable rule id (e.g. 'EM_DASH', 'CHATBOT'). Never shown raw in primary text. */
    ruleId: string;
    severity: VoiceFindingSeverity;
    /** 1-based touch the finding belongs to. */
    touchNumber: number;
    where: VoiceFindingWhere;
    /** The exact offending substring — quoted in the rail, highlighted inline. */
    match: string;
    /** Plain-language explanation from the rule. */
    message: string;
    /** True when this came from a per-brand rule (vs a universal rule) — drives the brand chip. */
    isBrandRule?: boolean;
}

/** Result of 'Outbound: Lint Sequence Copy'. */
export interface LintSequenceCopyResult {
    findings: VoiceFinding[];
    errorCount: number;
    warningCount: number;
}

/** Result of 'Outbound: Create Reply Sequence' as the builder consumes it. */
export interface CreateReplySequenceResult {
    /** Reply sequence id as a string (the builder treats it opaquely). */
    sequenceId: string;
    /** Deep link to the (paused) sequence in the Reply dashboard. */
    dashboardUrl: string;
    /** Parser + client + voice warnings, concatenated. Never blocking. */
    warnings: string[];
}

export abstract class OutboundActionClient {
    abstract getReplyStats(companyId: string): Promise<OutboundActionOutcome<OutboundReplyStats>>;
    abstract getReplySequences(companyId: string): Promise<OutboundActionOutcome<OutboundSequence[]>>;
    abstract getReplyEmailActivity(
        companyId: string,
        opts?: { dateRangePreset?: string; sequenceIds?: number[]; top?: number; skip?: number },
    ): Promise<OutboundActionOutcome<OutboundEmailActivityPage>>;
    abstract getSequenceDetail(companyId: string, sequenceId: number): Promise<OutboundActionOutcome<OutboundSequenceDetail>>;
    abstract syncStreams(companyId: string, dryRun: boolean): Promise<OutboundActionOutcome<OutboundSyncResult>>;
    abstract getApolloLists(companyId: string): Promise<OutboundActionOutcome<OutboundApolloList[]>>;
    /** Parse a markdown copy doc and create the sequence PAUSED. Never starts it. */
    abstract createSequence(companyId: string, copyDocument: string, nameOverride?: string): Promise<OutboundActionOutcome<OutboundCreateResult>>;
    /** Update an existing sequence's copy in place. DryRun=true returns the plan with zero writes. */
    abstract updateSequence(companyId: string, sequenceId: number, copyDocument: string, dryRun: boolean): Promise<OutboundActionOutcome<OutboundUpdateResult>>;
    /**
     * Update a SINGLE variant's subject + body in place, targeted by
     * (stepId, variantId). Never starts/unpauses the sequence — copy edit only.
     */
    abstract updateSequenceVariant(
        companyId: string,
        sequenceId: number,
        stepId: number,
        variantId: number,
        subject: string,
        message: string,
    ): Promise<OutboundActionOutcome<OutboundVariantUpdateResult>>;
    /** Enroll a reviewed, persona-bucketed people list. DryRun=true plans only; live enroll into an active sequence requires acknowledgeActiveSequence=true. */
    abstract enrollPeople(
        companyId: string,
        people: unknown[],
        personaSequenceMap: Record<string, number>,
        dryRun: boolean,
        acknowledgeActiveSequence: boolean,
    ): Promise<OutboundActionOutcome<OutboundEnrollResult>>;
    /**
     * Save the Apollo→Reply stream config for a company. Routes through the
     * gated 'Outbound: Save Stream Config' domain action (Operator+), which
     * resolves the CompanyIntegration from the authorized CompanyID and merges
     * Streams[] into Configuration server-side — preserving all other fields.
     */
    abstract saveStreamConfig(companyId: string, streams: OutboundStreamConfig[]): Promise<{ success: boolean; message: string }>;

    // ─── Phase 8 additions (#1071) ──────────────────────────────────────────
    /** List MarketingRecommendation rows for a company (newest-first). */
    abstract listRecommendations(companyId: string): Promise<OutboundActionOutcome<OutboundRecommendation[]>>;
    /** Apply a pause/scale recommendation by id. */
    abstract applyRecommendation(recommendationId: string, companyId: string): Promise<OutboundActionOutcome<OutboundApplyResult>>;
    /** Dismiss a recommendation via the gated 'Outbound: Dismiss Sequence Recommendation' action. */
    abstract dismissRecommendation(recommendationId: string, companyId: string): Promise<OutboundActionOutcome<{ dismissed: boolean }>>;
    /** Comparative per-variant rollup for one sequence. */
    abstract getVariantRollup(companyId: string, sequenceId: number): Promise<OutboundActionOutcome<OutboundVariantRollup>>;
    /** List OutboundMeeting rows (central pool) for a company. */
    abstract listOutboundMeetings(companyId: string): Promise<OutboundActionOutcome<OutboundPoolMeeting[]>>;
    /** Ingest booked meetings into the central pool (mock-fed default). */
    abstract ingestOutboundMeetings(companyId: string): Promise<OutboundActionOutcome<OutboundIngestResult>>;

    // ─── Sequence Builder (SA2) ──────────────────────────────────────────────
    /** Generate a draft sequence. CopyDocument is the source of truth; Steps are the editable cards. */
    abstract generateOutboundCopy(
        companyId: string,
        personaName: string,
        personaCriteria: string,
        stepCount: number,
    ): Promise<OutboundActionOutcome<GenerateOutboundCopyResult>>;
    /** Voice-check a serialized copy doc. Single source of truth lives on the server. */
    abstract lintSequenceCopy(companyId: string, copyDocument: string): Promise<OutboundActionOutcome<LintSequenceCopyResult>>;
    /** Create the sequence PAUSED in Reply from a serialized copy doc. Never starts it. */
    abstract createReplySequence(
        companyId: string,
        copyDocument: string,
        sequenceNameOverride?: string,
    ): Promise<OutboundActionOutcome<CreateReplySequenceResult>>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Runtime guards — RunAction's ResultData is a JSON string (NOT typed Params),
// so everything coming back is validated before it reaches the component.
// ─────────────────────────────────────────────────────────────────────────────

function isRecord(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** True when `v` is a canonical UUID string. */
function isUuid(v: string): boolean {
    return UUID_RE.test(v);
}

function numOr0(v: unknown): number {
    return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function strOr(v: unknown, fallback: string): string {
    return typeof v === 'string' ? v : fallback;
}

function isEmailOverviewLike(v: unknown): v is Record<string, unknown> {
    // Lenient on purpose: require the two load-bearing counters; everything
    // else defaults to 0 in toEmailOverview so additive server changes don't
    // break the panel.
    return isRecord(v) && typeof v['contacted'] === 'number' && typeof v['replied'] === 'number';
}

function toEmailOverview(v: Record<string, unknown>): OutboundEmailOverview {
    return {
        contacted: numOr0(v['contacted']),
        delivered: numOr0(v['delivered']),
        opened: numOr0(v['opened']),
        replied: numOr0(v['replied']),
        interested: numOr0(v['interested']),
        bounced: numOr0(v['bounced']),
        optedOut: numOr0(v['optedOut']),
        meetingsBooked: numOr0(v['meetingsBooked']),
        deliveredPercentage: numOr0(v['deliveredPercentage']),
        openedPercentage: numOr0(v['openedPercentage']),
        repliedPercentage: numOr0(v['repliedPercentage']),
        interestedPercentage: numOr0(v['interestedPercentage']),
        bouncedPercentage: numOr0(v['bouncedPercentage']),
    };
}

function toMeetings(v: unknown): OutboundMeeting[] {
    if (!Array.isArray(v)) return [];
    return v.filter(isRecord).map(m => ({
        contactFullName: strOr(m['contactFullName'], ''),
        sequenceName: strOr(m['sequenceName'], ''),
        bookedAt: strOr(m['bookedAt'], ''),
        source: strOr(m['source'], ''),
    }));
}

function numOrNull(v: unknown): number | null {
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Parse a step's variants. The owning step's id is threaded in (Reply nests it
 * on the parent step, not on each variant) so each variant carries the
 * (stepId, variantId) pair the in-place edit endpoint needs.
 */
function toStepVariants(v: unknown, stepId: number | null): OutboundStepVariant[] {
    if (!Array.isArray(v)) return [];
    return v.filter(isRecord).map(variant => ({
        variantId: numOrNull(variant['id']),
        stepId,
        subject: strOr(variant['subject'], ''),
        // Reply returns the variant body under `message`.
        body: strOr(variant['message'], ''),
        isEnabled: variant['isEnabled'] === true,
    }));
}

function toSequenceSteps(v: unknown): OutboundSequenceStep[] {
    if (!Array.isArray(v)) return [];
    const rawSteps = v.filter(isRecord);
    let cumulativeDelayMinutes = 0;
    return rawSteps.map((s, i) => {
        const dayFromStart = i === 0 ? 0 : Math.round(cumulativeDelayMinutes / 1440);
        const delay = numOr0(s['delayInMinutes']);
        cumulativeDelayMinutes += delay;
        return {
            stepNumber: i + 1,
            type: strOr(s['type'], 'email'),
            delayInMinutes: delay,
            dayFromStart,
            variants: toStepVariants(s['variants'], numOrNull(s['id'])),
        };
    });
}

function toStepPerf(raw: unknown): OutboundStepPerf[] {
    if (!Array.isArray(raw)) return [];
    return raw.filter(isRecord).map(s => ({
        stepNumber: numOr0(s['stepNumber']),
        stepDisplayName: strOr(s['stepDisplayName'], ''),
        sent: numOr0(s['sent']),
        opens: numOr0(s['opens']),
        openRate: numOr0(s['openRate']),
        replies: numOr0(s['replies']),
        replyRate: numOr0(s['replyRate']),
        interested: numOr0(s['interested']),
        bounced: numOr0(s['bounced']),
        optedOut: numOr0(s['optedOut']),
    }));
}

function toSequences(v: unknown): OutboundSequence[] {
    if (!Array.isArray(v)) return [];
    return v
        .filter(isRecord)
        .filter(s => typeof s['name'] === 'string')
        .map(s => ({
            id: numOr0(s['id']),
            name: strOr(s['name'], ''),
            status: strOr(s['status'], 'unknown'),
            isArchived: s['isArchived'] === true,
            created: strOr(s['created'], ''),
            health: strOr(s['health'], ''),
        }));
}

function toEmailActivity(v: unknown): OutboundEmailActivityRow[] {
    if (!Array.isArray(v)) return [];
    return v.filter(isRecord).map(r => ({
        contactId: numOr0(r['contactId']),
        firstName: strOr(r['firstName'], ''),
        lastName: strOr(r['lastName'], ''),
        email: strOr(r['email'], ''),
        company: strOr(r['company'], ''),
        sequenceId: numOr0(r['sequenceId']),
        sequenceName: strOr(r['sequenceName'], ''),
        sequenceStepNumber: numOr0(r['sequenceStepNumber']),
        sequenceStepDisplayName: strOr(r['sequenceStepDisplayName'], ''),
        emailAccountEmail: strOr(r['emailAccountEmail'], ''),
        deliveryDate: strOr(r['deliveryDate'], ''),
        isDelivered: r['isDelivered'] === true,
        isOpened: r['isOpened'] === true,
        isClicked: r['isClicked'] === true,
        isReplied: r['isReplied'] === true,
        isBounced: r['isBounced'] === true,
        isInterested: r['isInterested'] === true,
        isOptedOut: r['isOptedOut'] === true,
    }));
}

function toStringArray(v: unknown): string[] {
    if (!Array.isArray(v)) return [];
    return v.filter((s): s is string => typeof s === 'string');
}

function toCreatedSteps(v: unknown): OutboundCreatedStep[] {
    if (!Array.isArray(v)) return [];
    return v.filter(isRecord).map(s => ({
        touchNumber: numOr0(s['touchNumber']),
        stepId: typeof s['stepId'] === 'number' ? s['stepId'] : null,
        variantCount: numOr0(s['variantCount']),
        degradations: toStringArray(s['degradations']),
    }));
}

function toEnrollPlan(v: unknown, fallbackCompanyId: string): OutboundEnrollPlan {
    const r = isRecord(v) ? v : {};
    const personas: OutboundEnrollPersonaPlan[] = Array.isArray(r['personas'])
        ? r['personas'].filter(isRecord).map(p => ({
            persona: strOr(p['persona'], ''),
            sequenceId: numOr0(p['sequenceId']),
            contactCount: numOr0(p['contactCount']),
            contacts: Array.isArray(p['contacts'])
                ? p['contacts'].filter(isRecord).map(c => ({
                    email: strOr(c['email'], ''),
                    companyAcronym: typeof c['companyAcronym'] === 'string' ? c['companyAcronym'] : null,
                }))
                : [],
        }))
        : [];
    const orgAcronyms = Array.isArray(r['orgAcronyms'])
        ? r['orgAcronyms'].filter(isRecord).map(o => ({
            orgKey: strOr(o['orgKey'], ''),
            acronym: typeof o['acronym'] === 'string' ? o['acronym'] : null,
        }))
        : [];
    const skipped: OutboundEnrollSkipped[] = Array.isArray(r['skipped'])
        ? r['skipped'].filter(isRecord).map(s => ({
            email: typeof s['email'] === 'string' ? s['email'] : null,
            persona: typeof s['persona'] === 'string' ? s['persona'] : null,
            reason: strOr(s['reason'], ''),
        }))
        : [];
    return {
        companyId: strOr(r['companyId'], fallbackCompanyId),
        personas,
        orgAcronyms,
        skipped,
        totalPeople: numOr0(r['totalPeople']),
        eligibleContacts: numOr0(r['eligibleContacts']),
    };
}

// ─── Phase 8 parsing helpers (#1071) ────────────────────────────────────────

const RECOMMENDATION_OPS: readonly OutboundRecommendationOp[] = ['pause', 'scale', 'iterate'];

/**
 * Parse the `op` out of a MarketingRecommendation.ActionPayload. The payload is
 * stored as a JSON string on the entity; JSON.parse returns `any`, so the result
 * is treated as `unknown` and narrowed with a guard (no `as` on parse output).
 */
function parseRecommendationOp(actionPayload: unknown): OutboundRecommendationOp {
    if (typeof actionPayload !== 'string' || actionPayload.trim().length === 0) return 'unknown';
    let parsed: unknown;
    try {
        parsed = JSON.parse(actionPayload);
    } catch (err: unknown) {
        // Malformed payload → advisory; never throw from a read mapper. Log so a
        // systematically-bad ActionPayload is visible rather than silently 'unknown'.
        const msg = err instanceof Error ? err.message : String(err);
        LogError(`[OutboundActionClient] parseRecommendationOp: malformed ActionPayload JSON: ${msg}`);
        return 'unknown';
    }
    if (!isRecord(parsed)) return 'unknown';
    const op = parsed['op'];
    if (typeof op === 'string') {
        const lowered = op.toLowerCase();
        const match = RECOMMENDATION_OPS.find(o => o === lowered);
        if (match) return match;
    }
    return 'unknown';
}

function toRecommendations(v: unknown): OutboundRecommendation[] {
    if (!Array.isArray(v)) return [];
    return v.filter(isRecord).map(r => {
        const op = parseRecommendationOp(r['ActionPayload']);
        return {
            id: strOr(r['ID'], ''),
            category: strOr(r['Category'], ''),
            title: strOr(r['Title'], ''),
            detail: strOr(r['Detail'], ''),
            status: strOr(r['Status'], 'Open'),
            op,
            actionable: op === 'pause' || op === 'scale',
            createdAt: strOr(r['__mj_CreatedAt'], ''),
        };
    }).filter(r => r.id.length > 0);
}

function toPoolMeetings(v: unknown): OutboundPoolMeeting[] {
    if (!Array.isArray(v)) return [];
    return v.filter(isRecord).map(m => ({
        id: strOr(m['ID'], ''),
        sourceBrand: strOr(m['SourceBrand'], ''),
        prospectEmail: typeof m['ProspectEmail'] === 'string' ? m['ProspectEmail'] : null,
        sequenceId: numOrNull(m['SequenceID']),
        assignedRep: typeof m['AssignedRep'] === 'string' ? m['AssignedRep'] : null,
        bookedAt: typeof m['BookedAt'] === 'string' ? m['BookedAt'] : null,
        status: strOr(m['Status'], ''),
    })).filter(m => m.id.length > 0);
}

const VARIANT_VERDICTS: readonly OutboundVariantVerdict[] = ['KEEP', 'ITERATE', 'KILL', 'INSUFFICIENT_DATA'];

function toVariantVerdict(v: unknown): OutboundVariantVerdict {
    if (typeof v === 'string') {
        const match = VARIANT_VERDICTS.find(x => x === v);
        if (match) return match;
    }
    return 'INSUFFICIENT_DATA';
}

function toVariantGA4(v: unknown): OutboundVariantGA4 {
    const r = isRecord(v) ? v : {};
    return {
        sessions: numOr0(r['sessions']),
        engagedSessions: numOr0(r['engagedSessions']),
        conversions: numOr0(r['conversions']),
    };
}

function toVariantRollupRows(v: unknown): OutboundVariantRollupRow[] {
    if (!Array.isArray(v)) return [];
    return v.filter(isRecord).map(row => ({
        step: numOr0(row['step']),
        variantId: strOr(row['variantId'], ''),
        ga4: toVariantGA4(row['ga4']),
        stepReplyRate: numOrNull(row['stepReplyRate']),
        replyRateIsStepLevel: row['replyRateIsStepLevel'] !== false,
        note: strOr(row['note'], ''),
        verdict: toVariantVerdict(row['verdict']),
    }));
}

function toStepReplyRates(v: unknown): OutboundStepReplyRate[] {
    if (!Array.isArray(v)) return [];
    return v.filter(isRecord).map(s => ({
        step: numOr0(s['step']),
        stepDisplayName: strOr(s['stepDisplayName'], ''),
        sent: numOr0(s['sent']),
        replies: numOr0(s['replies']),
        replyRate: numOr0(s['replyRate']),
        winnerLabel: typeof s['winnerLabel'] === 'string' ? s['winnerLabel'] : null,
        comparisonLabel: strOr(s['comparisonLabel'], ''),
    }));
}

// ─── Sequence Builder parsing helpers (SA2) ─────────────────────────────────

/**
 * Resolve an output param that the server set to a JSON *string* (the action
 * serializes Steps/Findings via JSON.stringify). The transport may hand it back
 * either as the raw string or already-parsed (depending on simple-mode
 * serialization), so accept both. Returns `unknown` — callers narrow with a
 * shape guard (no `as` on parse output). Logs + returns null on malformed JSON.
 */
function parseJsonOutput(v: unknown, context: string): unknown {
    if (typeof v !== 'string') {
        // Already a value (array/object) — pass through for guard-based narrowing.
        return v;
    }
    if (v.trim().length === 0) return null;
    try {
        return JSON.parse(v);
    } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        LogError(`[OutboundActionClient] ${context}: malformed JSON output: ${msg}`);
        return null;
    }
}

function toBuilderSteps(v: unknown): BuilderStep[] {
    if (!Array.isArray(v)) return [];
    return v.filter(isRecord).map(s => ({
        delayDays: numOr0(s['delayDays']),
        delayLabel: strOr(s['delayLabel'], ''),
        subjects: toStringArray(s['subjects']),
        body: strOr(s['body'], ''),
    }));
}

function toVoiceSeverity(v: unknown): VoiceFindingSeverity {
    return v === 'error' ? 'error' : 'warning';
}

function toVoiceWhere(v: unknown): VoiceFindingWhere {
    return v === 'subject' ? 'subject' : 'body';
}

function toVoiceFindings(v: unknown): VoiceFinding[] {
    if (!Array.isArray(v)) return [];
    return v.filter(isRecord).map(f => ({
        ruleId: strOr(f['ruleId'], ''),
        severity: toVoiceSeverity(f['severity']),
        touchNumber: numOr0(f['touchNumber']),
        where: toVoiceWhere(f['where']),
        match: strOr(f['match'], ''),
        message: strOr(f['message'], ''),
    }));
}

// ─────────────────────────────────────────────────────────────────────────────
// GraphQL implementation
// ─────────────────────────────────────────────────────────────────────────────

interface RunActionCallResult {
    found: boolean;
    success: boolean;
    message: string;
    /** Output param Name → Value, extracted from the action result. Empty when none returned. */
    outputs: Map<string, unknown>;
}

/**
 * Non-'Outbound:'-prefixed actions the Phase 8 panels resolve by name. These are
 * hard-coded (never user-supplied) and only used inside the static IN(...) clause
 * in ensureActionIdMap — keep this list in sync with the registered action names.
 */
const EXTRA_ACTION_NAMES: readonly string[] = [
    'Query Marketing Recommendations',
    'Query Records',
];

/**
 * Extract output params from `ActionResult.Result`.
 *
 * NOTE on why this is still hand-rolled: GraphQLActionClient.RunAction puts the
 * server's *output* params into `ActionResult.Result` (the parsed `ResultData`),
 * NOT into `ActionResult.Params` — that array only echoes the *input* params.
 * And the server serializes outputs via CopyScalarsAndArrays in simple mode,
 * which turns the params ARRAY into an index-keyed OBJECT ({"0": {...}, "1": {...}}).
 * So we still untangle both shapes here; the client just spares us the raw
 * mutation + JSON.parse of the ResultData string.
 */
function extractOutputs(result: unknown): Map<string, unknown> {
    const outputs = new Map<string, unknown>();
    const entries: unknown[] = Array.isArray(result)
        ? result
        : isRecord(result) ? Object.values(result) : [];
    for (const p of entries) {
        if (isRecord(p) && typeof p['Name'] === 'string') {
            outputs.set(p['Name'], p['Value']);
        }
    }
    return outputs;
}

/**
 * Production OutboundActionClient — invokes MJ Actions via the framework's
 * GraphQLActionClient (transport: the official RunAction mutation + result
 * deserialization). This client owns only the Outbound-specific layer on top:
 * action Name → ID resolution, output-param extraction (see extractOutputs),
 * and the deployed / not-deployed / error outcome union.
 */
@Injectable()
export class GraphQLOutboundActionClient extends OutboundActionClient {
    // Action Name → ID for all 'Outbound:' actions, loaded once per session
    // (IDs are stable metadata). Lazily populated by ensureActionIdMap().
    private readonly actionIdCache = new Map<string, string>();
    private actionIdMapLoaded = false;

    async getReplyStats(companyId: string): Promise<OutboundActionOutcome<OutboundReplyStats>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        const call = await this.runAction('Outbound: Get Reply Stats', [
            { Name: 'CompanyID', Value: companyId },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };

        const rawOverview = call.outputs.get('EmailOverview');
        if (!isEmailOverviewLike(rawOverview)) {
            return { kind: 'error', message: `'Outbound: Get Reply Stats' succeeded but returned no parseable EmailOverview output (${call.message})` };
        }
        return {
            kind: 'success',
            data: {
                overview: toEmailOverview(rawOverview),
                meetings: toMeetings(call.outputs.get('Meetings')),
                integration: strOr(call.outputs.get('Integration'), ''),
            },
        };
    }

    async getSequenceDetail(companyId: string, sequenceId: number): Promise<OutboundActionOutcome<OutboundSequenceDetail>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        if (!sequenceId) return { kind: 'error', message: 'SequenceID is required' };
        const sequenceIdStr = String(sequenceId);
        const [contentsCall, stepStatsCall] = await Promise.all([
            this.runAction('Outbound: Get Sequence Contents', [
                { Name: 'CompanyID', Value: companyId },
                { Name: 'SequenceID', Value: sequenceIdStr },
            ]),
            this.runAction('Outbound: Get Sequence Step Stats', [
                { Name: 'CompanyID', Value: companyId },
                { Name: 'SequenceID', Value: sequenceIdStr },
                { Name: 'DateRangePreset', Value: 'allTime' },
            ]),
        ]);
        if (!contentsCall.found) {
            return { kind: 'not-deployed', message: contentsCall.message };
        }
        if (!contentsCall.success) return { kind: 'error', message: contentsCall.message };

        const steps = toSequenceSteps(contentsCall.outputs.get('Steps'));
        // Step stats may not be deployed yet — degrade gracefully to empty perf
        const perf = stepStatsCall.found && stepStatsCall.success
            ? toStepPerf(stepStatsCall.outputs.get('Steps'))
            : [];

        return { kind: 'success', data: { steps, perf } };
    }

    async syncStreams(companyId: string, dryRun: boolean): Promise<OutboundActionOutcome<OutboundSyncResult>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        const call = await this.runAction('Outbound: Sync Outbound Streams', [
            { Name: 'CompanyID', Value: companyId },
            { Name: 'DryRun', Value: String(dryRun) },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        const rawResults = call.outputs.get('Results');
        const results: OutboundStreamSyncResult[] = Array.isArray(rawResults)
            ? rawResults.filter(isRecord).map(r => ({
                apolloListName: strOr(r['apolloListName'], ''),
                personaName: strOr(r['personaName'], ''),
                replySequenceId: numOr0(r['replySequenceId']),
                apolloContacts: numOr0(r['apolloContacts']),
                imported: numOr0(r['imported']),
                importFailed: numOr0(r['importFailed']),
                alreadyEnrolled: numOr0(r['alreadyEnrolled']),
                enrolled: numOr0(r['enrolled']),
                enrollFailed: numOr0(r['enrollFailed']),
            }))
            : [];
        return {
            kind: 'success',
            data: {
                results,
                totalImported: numOr0(call.outputs.get('TotalImported')),
                totalEnrolled: numOr0(call.outputs.get('TotalEnrolled')),
                totalFailed: numOr0(call.outputs.get('TotalFailed')),
                wasDryRun: call.outputs.get('WasDryRun') === true,
            },
        };
    }

    async saveStreamConfig(companyId: string, streams: OutboundStreamConfig[]): Promise<{ success: boolean; message: string }> {
        if (!companyId) return { success: false, message: 'CompanyID is required' };
        const call = await this.runAction('Outbound: Save Stream Config', [
            { Name: 'CompanyID', Value: companyId },
            { Name: 'Streams', Value: JSON.stringify(streams) },
        ]);
        if (!call.found) return { success: false, message: `Save Stream Config action not deployed: ${call.message}` };
        return { success: call.success, message: call.message };
    }

    async getApolloLists(companyId: string): Promise<OutboundActionOutcome<OutboundApolloList[]>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        const call = await this.runAction('Outbound: Get Apollo Lists', [
            { Name: 'CompanyID', Value: companyId },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        const raw = call.outputs.get('Lists');
        const lists: OutboundApolloList[] = Array.isArray(raw)
            ? raw.filter(isRecord).map(l => ({
                id: strOr(l['id'], ''),
                name: strOr(l['name'], ''),
                kind: strOr(l['kind'], ''),
                cachedCount: numOr0(l['cachedCount']),
            }))
            : [];
        return { kind: 'success', data: lists };
    }

    async getReplySequences(companyId: string): Promise<OutboundActionOutcome<OutboundSequence[]>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        const call = await this.runAction('Outbound: Get Reply Sequences', [
            { Name: 'CompanyID', Value: companyId },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return { kind: 'success', data: toSequences(call.outputs.get('Sequences')) };
    }

    /**
     * Per-contact email engagement (who opened / clicked / replied) for the
     * brand, scoped server-side to its registered streams[] sequences. Pass
     * `sequenceIds` to narrow to one sequence; `top`/`skip` to page.
     */
    async getReplyEmailActivity(
        companyId: string,
        opts?: { dateRangePreset?: string; sequenceIds?: number[]; top?: number; skip?: number },
    ): Promise<OutboundActionOutcome<OutboundEmailActivityPage>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        const inputs: { Name: string; Value: string }[] = [{ Name: 'CompanyID', Value: companyId }];
        if (opts?.dateRangePreset) inputs.push({ Name: 'DateRangePreset', Value: opts.dateRangePreset });
        if (opts?.sequenceIds && opts.sequenceIds.length > 0) {
            inputs.push({ Name: 'SequenceIDs', Value: JSON.stringify(opts.sequenceIds) });
        }
        if (opts?.top !== undefined) inputs.push({ Name: 'Top', Value: String(opts.top) });
        if (opts?.skip !== undefined) inputs.push({ Name: 'Skip', Value: String(opts.skip) });
        const call = await this.runAction('Outbound: Get Email Activity', inputs);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                rows: toEmailActivity(call.outputs.get('Activity')),
                hasMore: call.outputs.get('HasMore') === true,
            },
        };
    }

    async createSequence(companyId: string, copyDocument: string, nameOverride?: string): Promise<OutboundActionOutcome<OutboundCreateResult>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        if (!copyDocument || copyDocument.trim().length === 0) {
            return { kind: 'error', message: 'CopyDocument is required' };
        }
        const inputs: { Name: string; Value: string }[] = [
            { Name: 'CompanyID', Value: companyId },
            { Name: 'CopyDocument', Value: copyDocument },
        ];
        if (nameOverride && nameOverride.trim().length > 0) {
            inputs.push({ Name: 'SequenceNameOverride', Value: nameOverride.trim() });
        }
        const call = await this.runAction('Outbound: Create Reply Sequence', inputs);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                sequenceId: numOr0(call.outputs.get('SequenceID')),
                status: strOr(call.outputs.get('Status'), 'unknown'),
                dashboardUrl: strOr(call.outputs.get('DashboardUrl'), ''),
                steps: toCreatedSteps(call.outputs.get('Steps')),
                warnings: toStringArray(call.outputs.get('Warnings')),
                integration: strOr(call.outputs.get('Integration'), ''),
            },
        };
    }

    async updateSequence(companyId: string, sequenceId: number, copyDocument: string, dryRun: boolean): Promise<OutboundActionOutcome<OutboundUpdateResult>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        if (!sequenceId) return { kind: 'error', message: 'SequenceID is required' };
        if (!copyDocument || copyDocument.trim().length === 0) {
            return { kind: 'error', message: 'CopyDocument is required' };
        }
        const call = await this.runAction('Outbound: Update Reply Sequence', [
            { Name: 'CompanyID', Value: companyId },
            { Name: 'SequenceID', Value: String(sequenceId) },
            { Name: 'CopyDocument', Value: copyDocument },
            { Name: 'DryRun', Value: String(dryRun) },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                sequenceId: numOr0(call.outputs.get('SequenceId')),
                stepCount: numOr0(call.outputs.get('StepCount')),
                updated: numOr0(call.outputs.get('Updated')),
                created: numOr0(call.outputs.get('Created')),
                warnings: toStringArray(call.outputs.get('Warnings')),
                wasDryRun: call.outputs.get('WasDryRun') === true,
                integration: strOr(call.outputs.get('Integration'), ''),
            },
        };
    }

    async updateSequenceVariant(
        companyId: string,
        sequenceId: number,
        stepId: number,
        variantId: number,
        subject: string,
        message: string,
    ): Promise<OutboundActionOutcome<OutboundVariantUpdateResult>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        if (!sequenceId) return { kind: 'error', message: 'SequenceID is required' };
        if (!stepId) return { kind: 'error', message: 'StepID is required' };
        if (!variantId) return { kind: 'error', message: 'VariantID is required' };
        const call = await this.runAction('Outbound: Update Sequence Variant', [
            { Name: 'CompanyID', Value: companyId },
            { Name: 'SequenceID', Value: String(sequenceId) },
            { Name: 'StepID', Value: String(stepId) },
            { Name: 'VariantID', Value: String(variantId) },
            { Name: 'Subject', Value: subject },
            { Name: 'Message', Value: message },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                updated: call.outputs.get('Updated') === true,
                integration: strOr(call.outputs.get('Integration'), ''),
            },
        };
    }

    async enrollPeople(
        companyId: string,
        people: unknown[],
        personaSequenceMap: Record<string, number>,
        dryRun: boolean,
        acknowledgeActiveSequence: boolean,
    ): Promise<OutboundActionOutcome<OutboundEnrollResult>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        if (!Array.isArray(people) || people.length === 0) {
            return { kind: 'error', message: 'People must be a non-empty array' };
        }
        if (!personaSequenceMap || Object.keys(personaSequenceMap).length === 0) {
            return { kind: 'error', message: 'PersonaSequenceMap must contain at least one persona → sequence mapping' };
        }
        const call = await this.runAction('Outbound: Enroll People Into Sequences', [
            { Name: 'CompanyID', Value: companyId },
            { Name: 'People', Value: JSON.stringify(people) },
            { Name: 'PersonaSequenceMap', Value: JSON.stringify(personaSequenceMap) },
            { Name: 'DryRun', Value: String(dryRun) },
            { Name: 'AcknowledgeActiveSequence', Value: String(acknowledgeActiveSequence) },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                plan: toEnrollPlan(call.outputs.get('Plan'), companyId),
                enrolledCount: numOr0(call.outputs.get('EnrolledCount')),
                wasDryRun: call.outputs.get('WasDryRun') === true,
                integration: strOr(call.outputs.get('Integration'), ''),
            },
        };
    }

    // ─── Phase 8: Recommendations panel (#1071) ─────────────────────────────

    async listRecommendations(companyId: string): Promise<OutboundActionOutcome<OutboundRecommendation[]>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        // Status omitted → the read action defaults to 'Open'; we want every
        // status so the panel can segment Open/Applied/Dismissed. The action
        // does not accept an "all" value, so query each status and merge.
        // Scope to Category='Channel' — the bucket outbound sequence learnings
        // write to — so this panel shows ONLY outbound recommendations, not the
        // company's strategy/onboarding recommendations (Experiment/Plan/etc.).
        const queryStatus = (status: string) =>
            this.runAction('Query Marketing Recommendations', [
                { Name: 'CompanyID', Value: companyId },
                { Name: 'Status', Value: status },
                { Name: 'Category', Value: 'Channel' },
            ]);
        const [open, applied, dismissed] = await Promise.all([
            queryStatus('Open'),
            queryStatus('Applied'),
            queryStatus('Dismissed'),
        ]);
        // The action either exists in this environment or it doesn't — the Open
        // call is the authority for the deployed / not-deployed decision.
        if (!open.found) return { kind: 'not-deployed', message: open.message };
        if (!open.success) return { kind: 'error', message: open.message };
        const merged = [
            ...toRecommendations(open.outputs.get('Recommendations')),
            ...(applied.found && applied.success ? toRecommendations(applied.outputs.get('Recommendations')) : []),
            ...(dismissed.found && dismissed.success ? toRecommendations(dismissed.outputs.get('Recommendations')) : []),
        ];
        return { kind: 'success', data: merged };
    }

    async applyRecommendation(recommendationId: string, companyId: string): Promise<OutboundActionOutcome<OutboundApplyResult>> {
        if (!recommendationId) return { kind: 'error', message: 'RecommendationID is required' };
        const inputs: { Name: string; Value: string }[] = [
            { Name: 'RecommendationID', Value: recommendationId },
        ];
        if (companyId) inputs.push({ Name: 'CompanyID', Value: companyId });
        const call = await this.runAction('Outbound: Apply Sequence Recommendation', inputs);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                applied: call.outputs.get('Applied') === true,
                op: strOr(call.outputs.get('Op'), ''),
                message: strOr(call.outputs.get('Message'), call.message),
                integration: strOr(call.outputs.get('Integration'), ''),
            },
        };
    }

    async dismissRecommendation(recommendationId: string, companyId: string): Promise<OutboundActionOutcome<{ dismissed: boolean }>> {
        if (!recommendationId) return { kind: 'error', message: 'RecommendationID is required' };
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        // Route through the gated domain action (Operator+), NOT generic Update
        // Record: the action enforces tenant access on CompanyID and cross-checks
        // it against the recommendation's own CompanyID, so a user can't dismiss
        // another tenant's recommendation.
        const call = await this.runAction('Outbound: Dismiss Sequence Recommendation', [
            { Name: 'RecommendationID', Value: recommendationId },
            { Name: 'CompanyID', Value: companyId },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return { kind: 'success', data: { dismissed: call.outputs.get('Dismissed') === true } };
    }

    // ─── Phase 8: Variant Rollup (#1071) ─────────────────────────────────────

    async getVariantRollup(companyId: string, sequenceId: number): Promise<OutboundActionOutcome<OutboundVariantRollup>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        if (!sequenceId) return { kind: 'error', message: 'SequenceID is required' };
        const call = await this.runAction('Outbound: Get Variant Rollup', [
            { Name: 'CompanyID', Value: companyId },
            { Name: 'SequenceID', Value: String(sequenceId) },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                variants: toVariantRollupRows(call.outputs.get('Variants')),
                stepReplyRates: toStepReplyRates(call.outputs.get('StepReplyRates')),
            },
        };
    }

    // ─── Phase 8: Central Pool (#1071) ───────────────────────────────────────

    async listOutboundMeetings(companyId: string): Promise<OutboundActionOutcome<OutboundPoolMeeting[]>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        // This reads the central OutboundMeeting pool via the generic 'Query
        // Records' action, which is NOT tenant-gated (per Marketing/CLAUDE.md) and
        // interpolates companyId into a SQL WHERE clause. companyId is mutable
        // browser state, so validate its UUID shape here rather than trusting
        // "validated upstream" — a non-UUID value would be a cross-tenant/injection sink.
        if (!isUuid(companyId)) return { kind: 'error', message: 'CompanyID must be a valid UUID' };
        const call = await this.runAction('Query Records', [
            { Name: 'EntityName', Value: 'Outbound Meetings' },
            { Name: 'ExtraFilter', Value: `CompanyID='${companyId}'` },
            { Name: 'OrderBy', Value: 'BookedAt DESC' },
            { Name: 'MaxRows', Value: '500' },
            { Name: 'ResultType', Value: 'simple' },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        // Query Records returns rows under a 'Records' output param.
        return { kind: 'success', data: toPoolMeetings(call.outputs.get('Records')) };
    }

    async ingestOutboundMeetings(companyId: string): Promise<OutboundActionOutcome<OutboundIngestResult>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        const call = await this.runAction('Outbound: Ingest Outbound Meetings', [
            { Name: 'CompanyID', Value: companyId },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                ingested: numOr0(call.outputs.get('Ingested')),
                skipped: numOr0(call.outputs.get('Skipped')),
                alreadyPresent: numOr0(call.outputs.get('AlreadyPresent')),
                wasDryRun: call.outputs.get('WasDryRun') === true,
                integration: strOr(call.outputs.get('Integration'), ''),
            },
        };
    }

    // ─── Sequence Builder (SA2) ──────────────────────────────────────────────

    async generateOutboundCopy(
        companyId: string,
        personaName: string,
        personaCriteria: string,
        stepCount: number,
    ): Promise<OutboundActionOutcome<GenerateOutboundCopyResult>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        if (!isUuid(companyId)) return { kind: 'error', message: 'CompanyID must be a valid UUID' };
        if (!personaName || personaName.trim().length === 0) return { kind: 'error', message: 'PersonaName is required' };
        if (!personaCriteria || personaCriteria.trim().length === 0) return { kind: 'error', message: 'PersonaCriteria is required' };
        const count = Number.isFinite(stepCount) ? Math.min(8, Math.max(1, Math.round(stepCount))) : 3;
        const call = await this.runAction('Outbound: Generate Outbound Copy', [
            { Name: 'CompanyID', Value: companyId },
            { Name: 'PersonaName', Value: personaName.trim() },
            { Name: 'PersonaCriteria', Value: personaCriteria.trim() },
            { Name: 'StepCount', Value: String(count) },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        const copyDocument = strOr(call.outputs.get('CopyDocument'), '');
        if (copyDocument.trim().length === 0) {
            return { kind: 'error', message: `'Outbound: Generate Outbound Copy' succeeded but returned an empty CopyDocument (${call.message})` };
        }
        return {
            kind: 'success',
            data: {
                copyDocument,
                steps: toBuilderSteps(parseJsonOutput(call.outputs.get('Steps'), 'generateOutboundCopy.Steps')),
                voiceWarnings: toStringArray(call.outputs.get('VoiceWarnings')),
                regenerated: call.outputs.get('Regenerated') === true,
            },
        };
    }

    async lintSequenceCopy(companyId: string, copyDocument: string): Promise<OutboundActionOutcome<LintSequenceCopyResult>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        if (!isUuid(companyId)) return { kind: 'error', message: 'CompanyID must be a valid UUID' };
        if (!copyDocument || copyDocument.trim().length === 0) return { kind: 'error', message: 'CopyDocument is required' };
        const call = await this.runAction('Outbound: Lint Sequence Copy', [
            { Name: 'CompanyID', Value: companyId },
            { Name: 'CopyDocument', Value: copyDocument },
        ]);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                findings: toVoiceFindings(parseJsonOutput(call.outputs.get('Findings'), 'lintSequenceCopy.Findings')),
                errorCount: numOr0(call.outputs.get('ErrorCount')),
                warningCount: numOr0(call.outputs.get('WarningCount')),
            },
        };
    }

    async createReplySequence(
        companyId: string,
        copyDocument: string,
        sequenceNameOverride?: string,
    ): Promise<OutboundActionOutcome<CreateReplySequenceResult>> {
        if (!companyId) return { kind: 'error', message: 'CompanyID is required' };
        if (!isUuid(companyId)) return { kind: 'error', message: 'CompanyID must be a valid UUID' };
        if (!copyDocument || copyDocument.trim().length === 0) return { kind: 'error', message: 'CopyDocument is required' };
        const inputs: { Name: string; Value: string }[] = [
            { Name: 'CompanyID', Value: companyId },
            { Name: 'CopyDocument', Value: copyDocument },
        ];
        if (sequenceNameOverride && sequenceNameOverride.trim().length > 0) {
            inputs.push({ Name: 'SequenceNameOverride', Value: sequenceNameOverride.trim() });
        }
        const call = await this.runAction('Outbound: Create Reply Sequence', inputs);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        // SequenceID may come back as a number or string — normalize to string.
        const rawId = call.outputs.get('SequenceID');
        const sequenceId = typeof rawId === 'number' ? String(rawId) : strOr(rawId, '');
        return {
            kind: 'success',
            data: {
                sequenceId,
                dashboardUrl: strOr(call.outputs.get('DashboardUrl'), ''),
                warnings: toStringArray(call.outputs.get('Warnings')),
            },
        };
    }

    /**
     * Load the Name → ID map for every 'Outbound:' action — plus the handful of
     * non-'Outbound:'-prefixed actions the Phase 8 panels need (the Marketing
     * recommendations read + the generic 'Query Records' read used for the
     * central-pool meetings) — in one query, once.
     *
     * The filter is a STATIC `LIKE` prefix OR an `IN (...)` over a fixed,
     * hard-coded name list — no caller-supplied value is interpolated into SQL,
     * so there's no escaping to hand-roll, and one batched read replaces a query
     * per action.
     */
    private async ensureActionIdMap(): Promise<void> {
        if (this.actionIdMapLoaded) return;
        const extraInClause = EXTRA_ACTION_NAMES.map(n => `'${n}'`).join(', ');
        const rv = new RunView();
        const result = await rv.RunView<{ ID: string; Name: string }>({
            EntityName: 'MJ: Actions',
            ExtraFilter: `Name LIKE 'Outbound: %' OR Name IN (${extraInClause})`,
            Fields: ['ID', 'Name'],
            ResultType: 'simple',
        });
        if (!result.Success) {
            throw new Error(result.ErrorMessage || 'Failed to load Outbound action metadata');
        }
        for (const row of result.Results ?? []) {
            this.actionIdCache.set(row.Name, row.ID);
        }
        this.actionIdMapLoaded = true;
    }

    private async resolveActionId(actionName: string): Promise<string | null> {
        await this.ensureActionIdMap();
        // null = action metadata not synced to this environment
        return this.actionIdCache.get(actionName) ?? null;
    }

    private async runAction(actionName: string, params: { Name: string; Value: string }[]): Promise<RunActionCallResult> {
        try {
            const actionId = await this.resolveActionId(actionName);
            if (!actionId) {
                return {
                    found: false,
                    success: false,
                    message: `Action '${actionName}' is not registered in this environment's metadata`,
                    outputs: new Map<string, unknown>(),
                };
            }
            const client = new GraphQLActionClient(Metadata.Provider as GraphQLDataProvider);
            const result = await client.RunAction(
                actionId,
                params.map(p => ({ Name: p.Name, Value: p.Value, Type: 'Input' as const })),
            );
            const success = result.Success === true;
            return {
                found: true,
                success,
                message: result.Message || (success ? 'OK' : 'Unknown error'),
                // Output params live in result.Result (parsed ResultData), NOT
                // result.Params (which echoes the inputs). See extractOutputs.
                outputs: extractOutputs(result.Result),
            };
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[OutboundActionClient] '${actionName}' failed: ${msg}`);
            return { found: true, success: false, message: msg, outputs: new Map<string, unknown>() };
        }
    }
}
